// frontend/src/components/MentorPanel.js
// NextScenes Mentor/Mentee foundation panel
// Phase 2A: choose a Moderator as mentor + Moderators see assigned mentees.
// Phase 2B: Moderator workspace reads assigned mentee storylines/scenes and leaves advisory notes.

import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useUser } from "../UserContext";

function safeTrim(v) {
  return String(v || "").trim();
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function currentLang() {
  try {
    return (window.localStorage.getItem("ns.lang") || "en").toLowerCase().startsWith("fr") ? "fr" : "en";
  } catch {
    return "en";
  }
}

const TEXT = {
  en: {
    title: "Mentorship",
    hubLabel: "Mentorship Hub",
    hubKicker: "Guided creativity",
    subtitle: "Choose a NextScenes Moderator as your writing mentor. The mentor guides; the writer still owns the work.",
    mentorIdentity: "Writing mentor",
    yourMentor: "Your selected guide",
    mentorNotesLabel: "Mentor guidance",
    menteeList: "Mentee list",
    menteeCount: "mentees",
    searchMentees: "Search mentees",
    searchMenteesPlaceholder: "Search by name or username…",
    sortMentees: "Sort",
    sortRecent: "Recently selected",
    sortName: "Name",
    noMenteeMatches: "No mentees match this search.",
    selectedMentee: "Selected mentee",
    chooseMentee: "Choose a mentee to open the mentor workspace.",
    selectedStorylineLabel: "Selected storyline",
    activeStorylineLabel: "Active storyline",
    noteCount: "notes",
    chooseMentor: "Choose mentor",
    noMentor: "No mentor selected",
    noModerators: "No moderators are available yet.",
    save: "Save mentor",
    saving: "Saving…",
    clear: "Clear mentor",
    currentMentor: "Current mentor",
    selected: "Selected",
    roleNote: "Moderator is a writing mentor role, not admin access.",
    loadedFailed: "Could not load mentorship data.",
    saved: "Mentor selection saved.",
    cleared: "Mentor cleared.",
    updateFailed: "Could not update mentor selection.",
    menteesTitle: "Your mentees",
    menteesSubtitle: "Writers who selected you as their Moderator will appear here.",
    noMentees: "No mentees have selected you yet.",
    selectedOn: "Selected on",
    viewProfile: "View profile",
    openWorkspace: "Mentor workspace",
    unknown: "Unknown",
    notesForYou: "Notes from your mentor",
    noNotesForYou: "No mentor notes yet.",
    workspaceTitle: "Mentor workspace",
    workspaceSubtitle: "Read-only guidance space for active, unfinished storylines. You may view assigned work and leave advisory notes, but you cannot change the writer’s text.",
    activeOnly: "Active storylines only. Completed or published books are hidden from mentorship workspaces.",
    hiddenClosed: "completed/published work hidden",
    closeWorkspace: "Close workspace",
    refreshWorkspace: "Refresh workspace",
    menteeStorylines: "Active mentee storylines",
    noStorylines: "No active mentoring storylines found for this mentee. Completed or published books are not shown here.",
    openStory: "Open",
    selectedStory: "Selected storyline",
    sceneCount: "scenes",
    canonCount: "canon",
    proposalCount: "proposals",
    readOnly: "Read-only",
    mentorGuardrail: "Mentor notes do not edit, submit, validate, or publish the writer’s work.",
    scenes: "Scenes",
    noScenes: "No scenes found in this storyline yet.",
    addNote: "Add mentor note",
    noteType: "Note type",
    noteGeneral: "General",
    noteSuggestion: "Suggestion",
    noteCorrection: "Correction",
    noteContinuity: "Continuity",
    noteEncouragement: "Encouragement",
    noteStoryWide: "Story-wide note",
    noteForScene: "Note for scene",
    noteText: "Mentor note",
    notePlaceholder: "Write a respectful suggestion, correction, or encouragement. The writer decides what to do with it.",
    saveNote: "Save note",
    noteSaved: "Mentor note saved. The writer’s work was not changed.",
    noteFailed: "Could not save mentor note.",
    notes: "Mentor notes",
    noWorkspaceNotes: "No notes for this storyline yet.",
    archive: "Archive",
    archived: "Mentor note archived.",
    archiveFailed: "Could not archive mentor note.",
    useThisScene: "Note on this scene",
    markRead: "Mark read",
    acceptGuidance: "Accept guidance",
    ignoreNote: "Ignore",
    archiveForMe: "Archive for me",
    copyNote: "Copy note",
    copiedNote: "Mentor note copied. Paste it into your draft if you want to use it.",
    noteUpdated: "Mentor note response saved. Your story text was not changed.",
    noteUpdateFailed: "Could not update mentor note response.",
    writerStatusNew: "New",
    writerStatusRead: "Read",
    writerStatusAccepted: "Accepted",
    writerStatusIgnored: "Ignored",
    writerStatusArchived: "Archived",
    writerDecisionReminder: "Accepting records your decision only. It does not edit the story.",
  },
  fr: {
    title: "Mentorat",
    hubLabel: "Espace mentorat",
    hubKicker: "Créativité guidée",
    subtitle: "Choisissez un Modérateur NextScenes comme mentor d’écriture. Le mentor guide ; l’auteur garde son œuvre.",
    mentorIdentity: "Mentor d’écriture",
    yourMentor: "Votre guide choisi",
    mentorNotesLabel: "Conseils du mentor",
    menteeList: "Liste des mentorés",
    menteeCount: "mentorés",
    searchMentees: "Rechercher des mentorés",
    searchMenteesPlaceholder: "Rechercher par nom ou identifiant…",
    sortMentees: "Trier",
    sortRecent: "Choix récents",
    sortName: "Nom",
    noMenteeMatches: "Aucun mentoré ne correspond à cette recherche.",
    selectedMentee: "Mentoré sélectionné",
    chooseMentee: "Choisissez un mentoré pour ouvrir l’espace mentor.",
    selectedStorylineLabel: "Storyline sélectionnée",
    activeStorylineLabel: "Storyline active",
    noteCount: "notes",
    chooseMentor: "Choisir un mentor",
    noMentor: "Aucun mentor sélectionné",
    noModerators: "Aucun modérateur n’est disponible pour le moment.",
    save: "Enregistrer le mentor",
    saving: "Enregistrement…",
    clear: "Retirer le mentor",
    currentMentor: "Mentor actuel",
    selected: "Sélectionné",
    roleNote: "Le rôle de Modérateur est un rôle de mentorat d’écriture, pas un accès admin.",
    loadedFailed: "Impossible de charger les données de mentorat.",
    saved: "Sélection du mentor enregistrée.",
    cleared: "Mentor retiré.",
    updateFailed: "Impossible de mettre à jour le mentor.",
    menteesTitle: "Vos mentorés",
    menteesSubtitle: "Les auteurs qui vous choisissent comme Modérateur apparaîtront ici.",
    noMentees: "Aucun mentoré ne vous a encore choisi.",
    selectedOn: "Choisi le",
    viewProfile: "Voir le profil",
    openWorkspace: "Espace mentor",
    unknown: "Inconnu",
    notesForYou: "Notes de votre mentor",
    noNotesForYou: "Aucune note de mentor pour le moment.",
    workspaceTitle: "Espace mentor",
    workspaceSubtitle: "Espace de conseil en lecture seule pour les storylines actives et non terminées. Vous pouvez voir le travail assigné et laisser des notes, sans modifier le texte de l’auteur.",
    activeOnly: "Storylines actives seulement. Les livres terminés ou publiés sont masqués dans l’espace mentor.",
    hiddenClosed: "œuvres terminées/publiées masquées",
    closeWorkspace: "Fermer l’espace",
    refreshWorkspace: "Actualiser l’espace",
    menteeStorylines: "Storylines actives du mentoré",
    noStorylines: "Aucune storyline active de mentorat pour ce mentoré. Les livres terminés ou publiés ne sont pas affichés ici.",
    openStory: "Ouvrir",
    selectedStory: "Storyline sélectionnée",
    sceneCount: "scènes",
    canonCount: "canon",
    proposalCount: "propositions",
    readOnly: "Lecture seule",
    mentorGuardrail: "Les notes du mentor ne modifient, ne soumettent, ne valident ni ne publient le travail de l’auteur.",
    scenes: "Scènes",
    noScenes: "Aucune scène trouvée dans cette storyline.",
    addNote: "Ajouter une note de mentor",
    noteType: "Type de note",
    noteGeneral: "Général",
    noteSuggestion: "Suggestion",
    noteCorrection: "Correction",
    noteContinuity: "Continuité",
    noteEncouragement: "Encouragement",
    noteStoryWide: "Note générale",
    noteForScene: "Note pour une scène",
    noteText: "Note du mentor",
    notePlaceholder: "Écrivez une suggestion, correction ou encouragement avec respect. L’auteur décide quoi en faire.",
    saveNote: "Enregistrer la note",
    noteSaved: "Note enregistrée. Le travail de l’auteur n’a pas été modifié.",
    noteFailed: "Impossible d’enregistrer la note.",
    notes: "Notes du mentor",
    noWorkspaceNotes: "Aucune note pour cette storyline.",
    archive: "Archiver",
    archived: "Note archivée.",
    archiveFailed: "Impossible d’archiver la note.",
    useThisScene: "Noter cette scène",
    markRead: "Marquer comme lu",
    acceptGuidance: "Accepter le conseil",
    ignoreNote: "Ignorer",
    archiveForMe: "Archiver pour moi",
    copyNote: "Copier la note",
    copiedNote: "Note copiée. Collez-la dans votre brouillon si vous voulez l’utiliser.",
    noteUpdated: "Réponse enregistrée. Le texte de votre histoire n’a pas été modifié.",
    noteUpdateFailed: "Impossible de mettre à jour la réponse à la note.",
    writerStatusNew: "Nouveau",
    writerStatusRead: "Lu",
    writerStatusAccepted: "Accepté",
    writerStatusIgnored: "Ignoré",
    writerStatusArchived: "Archivé",
    writerDecisionReminder: "Accepter enregistre seulement votre décision. Cela ne modifie pas l’histoire.",
  },
};

function fmtDate(raw) {
  try {
    const d = raw ? new Date(raw) : null;
    if (!d || Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function displayName(person, fallback = "Unknown") {
  return safeTrim(person?.name) || safeTrim(person?.username) || fallback;
}

function handle(person) {
  const u = safeTrim(person?.username);
  return u ? `@${u}` : "";
}

function shortText(text, max = 1200) {
  const s = safeTrim(text);
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max).trim()}…` : s;
}

function cardStyle() {
  return {
    border: "1px solid rgba(15, 81, 50, 0.16)",
    borderRadius: 20,
    padding: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%)",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
  };
}

function mentorCardStyle(active) {
  return {
    border: active ? "1px solid rgba(217, 164, 65, 0.72)" : "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 12,
    background: active ? "rgba(236, 253, 245, 0.92)" : "#ffffff",
    display: "grid",
    gridTemplateColumns: "44px 1fr auto",
    gap: 12,
    alignItems: "center",
  };
}

function avatarStyle() {
  return {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    background: "#f8fafc",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f172a",
    fontWeight: 760,
  };
}

function sectionBox(extra = {}) {
  return {
    border: "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 14,
    background: "#ffffff",
    ...extra,
  };
}

function smallPill(extra = {}) {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid rgba(15, 81, 50, 0.18)",
    borderRadius: 999,
    padding: "5px 9px",
    color: "#0f5132",
    background: "#ecfdf5",
    fontWeight: 600,
    fontSize: 12,
    ...extra,
  };
}


function mentorHubStyle() {
  return {
    border: "1px solid rgba(15, 81, 50, 0.18)",
    borderRadius: 26,
    padding: 18,
    background: "radial-gradient(circle at 10% 0%, rgba(15, 81, 50, 0.08), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f6fbf8 52%, #ffffff 100%)",
    boxShadow: "0 22px 50px rgba(15, 81, 50, 0.08)",
    position: "relative",
    overflow: "hidden",
  };
}

function mentorTitleBand() {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    border: "1px solid rgba(217, 164, 65, 0.42)",
    borderRadius: 999,
    padding: "7px 12px",
    background: "rgba(236, 253, 245, 0.95)",
    color: "#0f5132",
    fontWeight: 760,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    fontSize: 12,
  };
}

function mentorSectionBox(extra = {}) {
  return sectionBox({
    border: "1px solid rgba(15, 81, 50, 0.14)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%)",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)",
    ...extra,
  });
}

function selectedCardStyle(active, accent = "blue") {
  const isGold = accent === "gold";
  return {
    border: active
      ? `2px solid ${isGold ? "rgba(217, 164, 65, 0.95)" : "rgba(37, 99, 235, 0.95)"}`
      : "1px solid #e5e7eb",
    borderRadius: 18,
    padding: 14,
    background: active
      ? "linear-gradient(180deg, rgba(236, 253, 245, 0.98) 0%, #ffffff 100%)"
      : "#ffffff",
    boxShadow: active ? "0 16px 34px rgba(30, 64, 175, 0.12)" : "0 8px 18px rgba(15, 23, 42, 0.04)",
  };
}

function compactScrollBox(maxHeight = 430) {
  return {
    display: "grid",
    gap: 10,
    maxHeight,
    overflowY: "auto",
    paddingRight: 4,
  };
}

function Avatar({ person }) {
  const img = safeTrim(person?.avatarUrl);
  const name = displayName(person, "M");
  const initials = name.slice(0, 1).toUpperCase();

  return (
    <div style={avatarStyle()} aria-hidden="true">
      {img ? (
        <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

function noteStatusLabel(status, txt) {
  const s = safeTrim(status || "new").toLowerCase();
  if (s === "read") return txt.writerStatusRead;
  if (s === "accepted") return txt.writerStatusAccepted;
  if (s === "ignored") return txt.writerStatusIgnored;
  if (s === "archived") return txt.writerStatusArchived;
  return txt.writerStatusNew;
}

function noteStatusStyle(status) {
  const s = safeTrim(status || "new").toLowerCase();
  if (s === "accepted") {
    return smallPill({ borderColor: "#bbf7d0", color: "#166534", background: "#f0fdf4" });
  }
  if (s === "ignored" || s === "archived") {
    return smallPill({ borderColor: "#e5e7eb", color: "#475569", background: "#f8fafc" });
  }
  if (s === "read") {
    return smallPill({ borderColor: "#c7d2fe", color: "#3730a3", background: "#eef2ff" });
  }
  return smallPill({ borderColor: "#facc15", color: "#92400e", background: "#fffbeb" });
}

function NoteCard({ note, txt, canArchive, onArchive, canWriterRespond, onWriterAction, busy }) {
  if (!note) return null;

  const writerStatus = safeTrim(note.writerStatus || "new").toLowerCase() || "new";
  const isAccepted = writerStatus === "accepted";
  const isIgnored = writerStatus === "ignored";
  const isRead = writerStatus === "read" || isAccepted || isIgnored;

  return (
    <div
      className="ns-mentor-note-card"
      style={{
        border: isAccepted ? "1px solid #86efac" : "1px solid rgba(15, 81, 50, 0.14)",
        borderRadius: 18,
        padding: 14,
        background: isAccepted ? "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)" : "linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%)",
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span style={smallPill()}>{note.noteType || "general"}</span>
          {canWriterRespond ? <span style={noteStatusStyle(writerStatus)}>{noteStatusLabel(writerStatus, txt)}</span> : null}
          {note.storyTitle ? <span style={{ color: "#0f172a", fontWeight: 760 }}>{note.storyTitle}</span> : null}
          {note.sceneTitle ? <span style={{ color: "#475569", fontWeight: 600 }}>Scene: {note.sceneTitle}</span> : null}
        </div>
        <span style={{ color: "#64748b", fontWeight: 500, fontSize: 12 }}>{fmtDate(note.createdAt)}</span>
      </div>
      <p className="ns-mentor-note-text" style={{ margin: "10px 0 0", whiteSpace: "pre-wrap", color: "#1f2937", lineHeight: 1.6, fontWeight: 400 }}>
        {note.noteText}
      </p>

      {canWriterRespond ? (
        <div style={{ marginTop: 12 }}>
          <div style={{ color: "#64748b", fontWeight: 500, fontSize: 12, marginBottom: 8 }}>
            {txt.writerDecisionReminder}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {!isRead ? (
              <button className="btn ns-btn-soft" type="button" disabled={busy} onClick={() => onWriterAction(note, "read")}> 
                {txt.markRead}
              </button>
            ) : null}
            <button className="btn" type="button" disabled={busy || isAccepted} onClick={() => onWriterAction(note, "accept")}> 
              {txt.acceptGuidance}
            </button>
            <button className="btn ns-btn-soft" type="button" disabled={busy || isIgnored} onClick={() => onWriterAction(note, "ignore")}> 
              {txt.ignoreNote}
            </button>
            <button className="btn ns-btn-soft" type="button" disabled={busy} onClick={() => onWriterAction(note, "copy")}> 
              {txt.copyNote}
            </button>
            <button className="btn ns-btn-soft" type="button" disabled={busy} onClick={() => onWriterAction(note, "archive")}> 
              {txt.archiveForMe}
            </button>
          </div>
        </div>
      ) : null}

      {canArchive ? (
        <div style={{ marginTop: 10 }}>
          <button className="btn ns-btn-soft" type="button" disabled={busy} onClick={() => onArchive(note)}>
            {txt.archive}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function MentorPanel({ onNavigatePage }) {
  const { user, isAuthenticated, isModerator, isAdmin } = useUser() || {};
  const lang = currentLang();
  const txt = TEXT[lang] || TEXT.en;

  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [moderators, setModerators] = useState([]);
  const [mentor, setMentor] = useState(null);
  const [selectedMentorId, setSelectedMentorId] = useState("");
  const [mentees, setMentees] = useState([]);
  const [myNotes, setMyNotes] = useState([]);
  const [menteeQuery, setMenteeQuery] = useState("");
  const [menteeSort, setMenteeSort] = useState("recent");

  const [workspaceMentee, setWorkspaceMentee] = useState(null);
  const [workspaceStorylines, setWorkspaceStorylines] = useState([]);
  const [workspaceHiddenClosedCount, setWorkspaceHiddenClosedCount] = useState(0);
  const [workspaceStory, setWorkspaceStory] = useState(null);
  const [workspaceNotes, setWorkspaceNotes] = useState([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceErr, setWorkspaceErr] = useState("");

  const [noteBusy, setNoteBusy] = useState(false);
  const [noteType, setNoteType] = useState("general");
  const [noteSceneId, setNoteSceneId] = useState("");
  const [noteText, setNoteText] = useState("");

  const myId = useMemo(() => safeTrim(user?.id || user?._id), [user]);

  const selectableModerators = useMemo(() => {
    return safeArray(moderators).filter((m) => safeTrim(m?.id) && safeTrim(m?.id) !== myId);
  }, [moderators, myId]);

  const filteredMentees = useMemo(() => {
    const q = safeTrim(menteeQuery).toLowerCase();
    const rows = safeArray(mentees).filter((m) => {
      if (!q) return true;
      return `${displayName(m, "")} ${handle(m)}`.toLowerCase().includes(q);
    });

    return rows.sort((a, b) => {
      if (menteeSort === "name") {
        return displayName(a, txt.unknown).localeCompare(displayName(b, txt.unknown));
      }
      const da = new Date(a?.mentorSelectedAt || 0).getTime();
      const db = new Date(b?.mentorSelectedAt || 0).getTime();
      return db - da;
    });
  }, [mentees, menteeQuery, menteeSort, txt.unknown]);

  async function loadMyNotes() {
    try {
      const notesData = await api.get("/api/mentorship/my-notes");
      setMyNotes(safeArray(notesData?.notes));
    } catch {
      setMyNotes([]);
    }
  }

  async function loadMentorship() {
    if (!isAuthenticated) return;

    setLoading(true);
    setErr("");
    setOk("");

    try {
      const [modsData, mineData, notesData] = await Promise.all([
        api.get("/api/mentorship/moderators"),
        api.get("/api/mentorship/mine"),
        api.get("/api/mentorship/my-notes").catch(() => ({ notes: [] })),
      ]);

      const mods = safeArray(modsData?.moderators);
      const currentMentor = mineData?.mentor || null;

      setModerators(mods);
      setMentor(currentMentor);
      setSelectedMentorId(safeTrim(currentMentor?.id || mineData?.mentorId));
      setMyNotes(safeArray(notesData?.notes));

      if (isModerator || isAdmin) {
        try {
          const menteesData = await api.get("/api/mentorship/mentees");
          setMentees(safeArray(menteesData?.mentees));
        } catch {
          setMentees([]);
        }
      } else {
        setMentees([]);
      }
    } catch (e) {
      setErr(e?.message || txt.loadedFailed);
      setModerators([]);
      setMentor(null);
      setSelectedMentorId("");
      setMentees([]);
      setMyNotes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMentorship();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isModerator, isAdmin, myId]);

  async function saveMentor() {
    if (!isAuthenticated) return;

    setBusy(true);
    setErr("");
    setOk("");

    try {
      const data = await api.patch("/api/mentorship/mine", { mentorId: selectedMentorId });
      const nextMentor = data?.mentor || null;
      setMentor(nextMentor);
      setSelectedMentorId(safeTrim(nextMentor?.id || data?.mentorId));
      setOk(nextMentor ? txt.saved : txt.cleared);
      await loadMyNotes();

      if (isModerator || isAdmin) {
        try {
          const menteesData = await api.get("/api/mentorship/mentees");
          setMentees(safeArray(menteesData?.mentees));
        } catch {}
      }
    } catch (e) {
      setErr(e?.message || txt.updateFailed);
    } finally {
      setBusy(false);
    }
  }

  async function clearMentor() {
    setSelectedMentorId("");
    setBusy(true);
    setErr("");
    setOk("");
    try {
      await api.patch("/api/mentorship/mine", { mentorId: "" });
      setMentor(null);
      setMyNotes([]);
      setOk(txt.cleared);
    } catch (e) {
      setErr(e?.message || txt.updateFailed);
    } finally {
      setBusy(false);
    }
  }

  function openPublicProfile(person) {
    const username = safeTrim(person?.username);
    if (!username) return;

    if (onNavigatePage) {
      onNavigatePage("publicProfile", { username });
      return;
    }

    window.location.href = `/u/${encodeURIComponent(username)}`;
  }

  async function openMentorWorkspace(mentee) {
    if (!mentee?.id) return;
    setWorkspaceMentee(mentee);
    setWorkspaceStorylines([]);
    setWorkspaceHiddenClosedCount(0);
    setWorkspaceStory(null);
    setWorkspaceNotes([]);
    setWorkspaceErr("");
    setNoteText("");
    setNoteSceneId("");
    setNoteType("general");
    setWorkspaceLoading(true);

    try {
      const data = await api.get(`/api/mentorship/mentees/${encodeURIComponent(mentee.id)}/storylines`);
      setWorkspaceMentee(data?.mentee || mentee);
      setWorkspaceStorylines(safeArray(data?.storylines));
      setWorkspaceHiddenClosedCount(Number(data?.hiddenClosedCount || 0));
    } catch (e) {
      setWorkspaceErr(e?.message || txt.loadedFailed);
    } finally {
      setWorkspaceLoading(false);
    }
  }

  async function loadWorkspaceStory(story) {
    if (!workspaceMentee?.id || !story?.id) return;
    setWorkspaceLoading(true);
    setWorkspaceErr("");
    setWorkspaceStory(null);
    setWorkspaceNotes([]);
    setNoteText("");
    setNoteSceneId("");

    try {
      const data = await api.get(
        `/api/mentorship/mentees/${encodeURIComponent(workspaceMentee.id)}/storylines/${encodeURIComponent(story.id)}`
      );
      setWorkspaceStory(data?.story || null);
      setWorkspaceNotes(safeArray(data?.notes));
    } catch (e) {
      setWorkspaceErr(e?.message || txt.loadedFailed);
    } finally {
      setWorkspaceLoading(false);
    }
  }

  async function refreshWorkspace() {
    if (!workspaceMentee?.id) return;
    const currentStoryId = safeTrim(workspaceStory?.id);
    await openMentorWorkspace(workspaceMentee);
    if (currentStoryId) {
      await loadWorkspaceStory({ id: currentStoryId });
    }
  }

  async function saveMentorNote() {
    if (!workspaceMentee?.id || !workspaceStory?.id || !safeTrim(noteText)) return;
    setNoteBusy(true);
    setWorkspaceErr("");
    setOk("");

    try {
      const data = await api.post(`/api/mentorship/mentees/${encodeURIComponent(workspaceMentee.id)}/notes`, {
        storyId: workspaceStory.id,
        sceneId: noteSceneId,
        noteType,
        noteText,
      });
      const newNote = data?.note;
      if (newNote) setWorkspaceNotes((prev) => [newNote, ...safeArray(prev)]);
      setNoteText("");
      setNoteSceneId("");
      setNoteType("general");
      setOk(txt.noteSaved);
    } catch (e) {
      setWorkspaceErr(e?.message || txt.noteFailed);
    } finally {
      setNoteBusy(false);
    }
  }

  async function archiveNote(note) {
    const noteId = safeTrim(note?.id);
    if (!noteId) return;
    setNoteBusy(true);
    setWorkspaceErr("");
    setOk("");

    try {
      await api.del(`/api/mentorship/notes/${encodeURIComponent(noteId)}`);
      setWorkspaceNotes((prev) => safeArray(prev).filter((n) => safeTrim(n?.id) !== noteId));
      setOk(txt.archived);
    } catch (e) {
      setWorkspaceErr(e?.message || txt.archiveFailed);
    } finally {
      setNoteBusy(false);
    }
  }

  async function copyNoteText(note) {
    const text = safeTrim(note?.noteText);
    if (!text) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setOk(txt.copiedNote);
        return;
      }
    } catch {}

    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "readonly");
      area.style.position = "fixed";
      area.style.left = "-9999px";
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);
      setOk(txt.copiedNote);
    } catch {
      setOk("");
    }
  }

  async function respondToMyNote(note, action) {
    const noteId = safeTrim(note?.id);
    const cleanAction = safeTrim(action).toLowerCase();
    if (!noteId || !cleanAction) return;

    if (cleanAction === "copy") {
      await copyNoteText(note);
      return;
    }

    setBusy(true);
    setErr("");
    setOk("");

    try {
      const data = await api.patch(`/api/mentorship/my-notes/${encodeURIComponent(noteId)}`, { action: cleanAction });
      const updated = data?.note;

      if (cleanAction === "archive") {
        setMyNotes((prev) => safeArray(prev).filter((n) => safeTrim(n?.id) !== noteId));
      } else if (updated) {
        setMyNotes((prev) => safeArray(prev).map((n) => (safeTrim(n?.id) === noteId ? updated : n)));
      }

      setOk(txt.noteUpdated);
    } catch (e) {
      setErr(e?.message || txt.noteUpdateFailed);
    } finally {
      setBusy(false);
    }
  }

  if (!isAuthenticated) return null;

  return (
    <section className="ns-mentor-hub" style={{ marginTop: 18, ...mentorHubStyle() }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ maxWidth: 880 }}>
          <span style={mentorTitleBand()}>✦ {txt.hubKicker}</span>
          <h3 style={{ margin: "10px 0 0", color: "#0f172a", fontSize: 28, letterSpacing: "-0.03em" }}>
            {txt.hubLabel}
          </h3>
          <p style={{ margin: "6px 0 0", color: "#334155", fontWeight: 500, lineHeight: 1.55 }}>
            {txt.subtitle}
          </p>
          <p style={{ margin: "6px 0 0", color: "#0f5132", fontWeight: 760, fontSize: 13 }}>
            {txt.roleNote}
          </p>
        </div>
        <button className="btn ns-btn-soft" type="button" onClick={loadMentorship} disabled={loading || busy}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err ? <div className="banner" style={{ marginTop: 12 }}>{err}</div> : null}
      {ok ? <div className="banner" style={{ marginTop: 12 }}>{ok}</div> : null}

      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: isModerator || isAdmin ? "repeat(auto-fit, minmax(280px, 1fr))" : "1fr",
          gap: 14,
        }}
      >
        <div style={mentorSectionBox()}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ color: "#0f5132", fontSize: 12, fontWeight: 760, textTransform: "uppercase", letterSpacing: "0.10em" }}>
                {txt.yourMentor}
              </div>
              <h4 style={{ margin: "4px 0 0", color: "#0f172a", fontSize: 20 }}>{txt.currentMentor}</h4>
            </div>
            <span style={smallPill({ borderColor: "#fde68a", color: "#92400e", background: "#fffbeb" })}>
              {txt.mentorIdentity}
            </span>
          </div>

          {mentor ? (
            <div style={{ ...mentorCardStyle(true), ...selectedCardStyle(true, "gold") }}>
              <Avatar person={mentor} />
              <div>
                <div style={{ fontWeight: 760, color: "#0f172a", fontSize: 18 }}>{displayName(mentor, txt.unknown)}</div>
                <div style={{ color: "#64748b", fontWeight: 600, fontSize: 13 }}>{handle(mentor)}</div>
              </div>
              <span style={{ ...smallPill({ borderColor: "#fde68a", color: "#92400e", background: "#fffbeb" }) }}>
                {txt.selected}
              </span>
            </div>
          ) : (
            <div style={{ border: "1px dashed #cbd5e1", borderRadius: 16, padding: 14, color: "#64748b", fontWeight: 600, background: "#f8fafc" }}>
              {txt.noMentor}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label style={{ display: "block", fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>
              {txt.chooseMentor}
            </label>
            <select
              value={selectedMentorId}
              onChange={(e) => setSelectedMentorId(e.target.value)}
              disabled={busy || loading || selectableModerators.length === 0}
              style={{
                width: "100%",
                padding: 10,
                borderRadius: 12,
                border: "1px solid #d1d5db",
                background: "#ffffff",
                fontWeight: 500,
              }}
            >
              <option value="">{txt.noMentor}</option>
              {selectableModerators.map((m) => (
                <option key={m.id} value={m.id}>
                  {displayName(m, txt.unknown)} {handle(m) ? `(${handle(m)})` : ""}
                </option>
              ))}
            </select>

            {selectableModerators.length === 0 ? (
              <div style={{ marginTop: 8, color: "#64748b", fontWeight: 500 }}>{txt.noModerators}</div>
            ) : null}

            <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn" type="button" onClick={saveMentor} disabled={busy || loading}>
                {busy ? txt.saving : txt.save}
              </button>
              <button
                className="btn ns-btn-soft"
                type="button"
                onClick={clearMentor}
                disabled={busy || loading || (!selectedMentorId && !mentor)}
              >
                {txt.clear}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 18, borderTop: "1px solid rgba(37, 99, 235, 0.14)", paddingTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ color: "#0f5132", fontSize: 12, fontWeight: 760, textTransform: "uppercase", letterSpacing: "0.10em" }}>
                  {txt.mentorNotesLabel}
                </div>
                <h4 style={{ margin: "4px 0 0", color: "#0f172a", fontSize: 19 }}>{txt.notesForYou}</h4>
              </div>
              {safeArray(myNotes).length > 0 ? (
                <span style={smallPill({ borderColor: "#c7d2fe", color: "#3730a3", background: "#eef2ff" })}>
                  {safeArray(myNotes).length} {txt.noteCount}
                </span>
              ) : null}
            </div>
            {safeArray(myNotes).length === 0 ? (
              <div style={{ border: "1px dashed #cbd5e1", borderRadius: 16, padding: 14, color: "#64748b", fontWeight: 600, background: "#f8fafc" }}>
                {txt.noNotesForYou}
              </div>
            ) : (
              <div style={compactScrollBox(520)}>
                {safeArray(myNotes).slice(0, 12).map((note) => (
                  <NoteCard key={note.id} note={note} txt={txt} canArchive={false} canWriterRespond onWriterAction={respondToMyNote} busy={busy} />
                ))}
              </div>
            )}
          </div>
        </div>

        {(isModerator || isAdmin) ? (
          <div style={mentorSectionBox()}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ color: "#0f5132", fontSize: 12, fontWeight: 760, textTransform: "uppercase", letterSpacing: "0.10em" }}>
                  {txt.menteeList}
                </div>
                <h4 style={{ margin: "4px 0 0", color: "#0f172a", fontSize: 20 }}>{txt.menteesTitle}</h4>
                <p style={{ margin: "6px 0 0", color: "#64748b", fontWeight: 500, lineHeight: 1.45 }}>{txt.menteesSubtitle}</p>
              </div>
              <span style={smallPill({ borderColor: "#c7d2fe", color: "#3730a3", background: "#eef2ff" })}>
                {safeArray(mentees).length} {txt.menteeCount}
              </span>
            </div>

            {safeArray(mentees).length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 1fr) minmax(140px, 0.55fr)", gap: 10, marginBottom: 12 }}>
                <label style={{ display: "grid", gap: 5, color: "#475569", fontWeight: 600, fontSize: 12 }}>
                  {txt.searchMentees}
                  <input
                    value={menteeQuery}
                    onChange={(e) => setMenteeQuery(e.target.value)}
                    placeholder={txt.searchMenteesPlaceholder}
                    style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: 10, fontWeight: 500 }}
                  />
                </label>
                <label style={{ display: "grid", gap: 5, color: "#475569", fontWeight: 600, fontSize: 12 }}>
                  {txt.sortMentees}
                  <select
                    value={menteeSort}
                    onChange={(e) => setMenteeSort(e.target.value)}
                    style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: 10, fontWeight: 500, background: "#ffffff" }}
                  >
                    <option value="recent">{txt.sortRecent}</option>
                    <option value="name">{txt.sortName}</option>
                  </select>
                </label>
              </div>
            ) : null}

            {safeArray(mentees).length === 0 ? (
              <div style={{ border: "1px dashed #cbd5e1", borderRadius: 16, padding: 14, color: "#64748b", fontWeight: 600, background: "#f8fafc" }}>{txt.noMentees}</div>
            ) : filteredMentees.length === 0 ? (
              <div style={{ border: "1px dashed #cbd5e1", borderRadius: 16, padding: 14, color: "#64748b", fontWeight: 600, background: "#f8fafc" }}>{txt.noMenteeMatches}</div>
            ) : (
              <div style={compactScrollBox(455)}>
                {filteredMentees.map((m) => {
                  const activeMentee = safeTrim(m?.id) && safeTrim(m?.id) === safeTrim(workspaceMentee?.id);
                  return (
                    <div key={m.id} style={{ ...mentorCardStyle(activeMentee), ...selectedCardStyle(activeMentee, "gold") }}>
                      <Avatar person={m} />
                      <div>
                        <div style={{ fontWeight: 760, color: "#0f172a", fontSize: 17 }}>{displayName(m, txt.unknown)}</div>
                        <div style={{ color: "#64748b", fontWeight: 600, fontSize: 13 }}>{handle(m)}</div>
                        <div style={{ color: "#64748b", fontWeight: 500, fontSize: 12 }}>
                          {txt.selectedOn}: {fmtDate(m.mentorSelectedAt)}
                        </div>
                        {activeMentee ? (
                          <div style={{ marginTop: 6 }}>
                            <span style={smallPill({ borderColor: "#fde68a", color: "#92400e", background: "#fffbeb" })}>
                              {txt.selectedMentee}
                            </span>
                          </div>
                        ) : null}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <button className="btn ns-btn-soft" type="button" onClick={() => openPublicProfile(m)}>
                          {txt.viewProfile}
                        </button>
                        <button className="btn" type="button" onClick={() => openMentorWorkspace(m)}>
                          {txt.openWorkspace}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {(isModerator || isAdmin) && workspaceMentee ? (
        <div style={{ marginTop: 18, ...mentorSectionBox({ borderColor: "rgba(37, 99, 235, 0.28)", background: "linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%)" }) }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div>
              <span style={smallPill()}>{txt.readOnly}</span>
              <h3 style={{ margin: "10px 0 0", color: "#1e3a8a", fontSize: 24, letterSpacing: "-0.02em" }}>
                {txt.workspaceTitle}: {displayName(workspaceMentee, txt.unknown)}
              </h3>
              <p style={{ margin: "6px 0 0", color: "#475569", fontWeight: 500, lineHeight: 1.5 }}>{txt.workspaceSubtitle}</p>
              <p style={{ margin: "6px 0 0", color: "#92400e", fontWeight: 600 }}>{txt.mentorGuardrail}</p>
              <p style={{ margin: "6px 0 0", color: "#1d4ed8", fontWeight: 600 }}>{txt.activeOnly}</p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn ns-btn-soft" type="button" onClick={refreshWorkspace} disabled={workspaceLoading || noteBusy}>
                {txt.refreshWorkspace}
              </button>
              <button
                className="btn ns-btn-soft"
                type="button"
                onClick={() => {
                  setWorkspaceMentee(null);
                  setWorkspaceStorylines([]);
                  setWorkspaceHiddenClosedCount(0);
                  setWorkspaceStory(null);
                  setWorkspaceNotes([]);
                  setWorkspaceErr("");
                }}
              >
                {txt.closeWorkspace}
              </button>
            </div>
          </div>

          {workspaceErr ? <div className="banner" style={{ marginTop: 12 }}>{workspaceErr}</div> : null}

          <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "minmax(280px, 0.9fr) minmax(340px, 1.4fr)", gap: 16 }}>
            <div style={mentorSectionBox({ background: "#ffffff" })}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 10 }}>
                <h4 style={{ margin: 0, color: "#0f172a" }}>{txt.menteeStorylines}</h4>
                {workspaceHiddenClosedCount > 0 ? (
                  <span style={smallPill({ borderColor: "#fde68a", color: "#92400e", background: "#fffbeb" })}>
                    {workspaceHiddenClosedCount} {txt.hiddenClosed}
                  </span>
                ) : null}
              </div>
              {workspaceLoading && !workspaceStorylines.length ? <div style={{ color: "#64748b", fontWeight: 600 }}>Loading…</div> : null}
              {!workspaceLoading && safeArray(workspaceStorylines).length === 0 ? (
                <div style={{ color: "#64748b", fontWeight: 600 }}>{txt.noStorylines}</div>
              ) : (
                <div style={compactScrollBox(520)}>
                  {safeArray(workspaceStorylines).map((story) => {
                    const active = safeTrim(story?.id) === safeTrim(workspaceStory?.id);
                    return (
                      <button
                        key={story.id}
                        type="button"
                        onClick={() => loadWorkspaceStory(story)}
                        style={{
                          textAlign: "left",
                          ...selectedCardStyle(active, "blue"),
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                          <div style={{ fontWeight: 760, color: "#0f172a", fontSize: 16 }}>{story.title}</div>
                          {active ? (
                            <span style={smallPill({ borderColor: "#bfdbfe", color: "#1d4ed8", background: "#eff6ff" })}>
                              {txt.activeStorylineLabel}
                            </span>
                          ) : null}
                        </div>
                        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                          <span style={smallPill()}>{story.writingMode || "Solo"}</span>
                          <span style={smallPill()}>{Number(story.sceneCount || 0)} {txt.sceneCount}</span>
                          <span style={smallPill()}>{Number(story.canonCount || 0)} {txt.canonCount}</span>
                          <span style={smallPill()}>{Number(story.proposalCount || 0)} {txt.proposalCount}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={mentorSectionBox({ background: "#ffffff" })}>
              {!workspaceStory ? (
                <div style={{ border: "1px dashed #cbd5e1", borderRadius: 18, padding: 22, color: "#64748b", fontWeight: 600, background: "#f8fafc" }}>
                  {txt.openStory} {txt.menteeStorylines.toLowerCase()}.
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <span style={smallPill({ borderColor: "#bfdbfe", color: "#1d4ed8", background: "#eff6ff" })}>{txt.selectedStorylineLabel}</span>
                      <h3 style={{ margin: "8px 0 0", color: "#0f172a" }}>{workspaceStory.title}</h3>
                      <p style={{ margin: "6px 0 0", color: "#64748b", fontWeight: 500 }}>{workspaceStory.description || workspaceStory.synopsis || ""}</p>
                    </div>
                  </div>

                  <div style={{ marginTop: 14, ...sectionBox({ background: "#fffdf7", borderColor: "#fde68a" }) }}>
                    <h4 style={{ margin: "0 0 10px", color: "#0f172a" }}>{txt.addNote}</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                      <label style={{ display: "grid", gap: 6, fontWeight: 600, color: "#334155" }}>
                        {txt.noteType}
                        <select value={noteType} onChange={(e) => setNoteType(e.target.value)} style={{ padding: 10, borderRadius: 12, border: "1px solid #d1d5db" }}>
                          <option value="general">{txt.noteGeneral}</option>
                          <option value="suggestion">{txt.noteSuggestion}</option>
                          <option value="correction">{txt.noteCorrection}</option>
                          <option value="continuity">{txt.noteContinuity}</option>
                          <option value="encouragement">{txt.noteEncouragement}</option>
                        </select>
                      </label>
                      <label style={{ display: "grid", gap: 6, fontWeight: 600, color: "#334155" }}>
                        {txt.noteForScene}
                        <select value={noteSceneId} onChange={(e) => setNoteSceneId(e.target.value)} style={{ padding: 10, borderRadius: 12, border: "1px solid #d1d5db" }}>
                          <option value="">{txt.noteStoryWide}</option>
                          {safeArray(workspaceStory.scenes).map((scene, idx) => (
                            <option key={scene.id || idx} value={scene.id}>
                              {scene.sceneNumber ? `Scene ${scene.sceneNumber}: ` : ""}{scene.title || `Scene ${idx + 1}`}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <label style={{ display: "grid", gap: 6, marginTop: 10, fontWeight: 600, color: "#334155" }}>
                      {txt.noteText}
                      <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder={txt.notePlaceholder}
                        rows={4}
                        maxLength={2400}
                        style={{ padding: 12, borderRadius: 14, border: "1px solid #d1d5db", resize: "vertical", lineHeight: 1.5 }}
                      />
                    </label>
                    <div style={{ marginTop: 10 }}>
                      <button className="btn" type="button" onClick={saveMentorNote} disabled={noteBusy || !safeTrim(noteText)}>
                        {noteBusy ? txt.saving : txt.saveNote}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <h4 style={{ margin: "0 0 10px", color: "#0f172a" }}>{txt.scenes}</h4>
                    {safeArray(workspaceStory.scenes).length === 0 ? (
                      <div style={{ color: "#64748b", fontWeight: 600 }}>{txt.noScenes}</div>
                    ) : (
                      <div style={{ display: "grid", gap: 12 }}>
                        {safeArray(workspaceStory.scenes).map((scene, idx) => (
                          <article key={scene.id || idx} style={{ border: "1px solid #e5e7eb", borderRadius: 16, padding: 12, background: "#ffffff" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                              <div>
                                <div style={{ fontWeight: 760, color: "#0f172a" }}>
                                  {scene.sceneNumber ? `Scene ${scene.sceneNumber}: ` : ""}{scene.title || `Scene ${idx + 1}`}
                                </div>
                                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 500, marginTop: 3 }}>
                                  {scene.type || "scene"} {scene.status ? `· ${scene.status}` : ""}
                                </div>
                              </div>
                              <button
                                className="btn ns-btn-soft"
                                type="button"
                                onClick={() => {
                                  setNoteSceneId(scene.id || "");
                                  setNoteType("suggestion");
                                  setNoteText("");
                                }}
                              >
                                {txt.useThisScene}
                              </button>
                            </div>
                            <p style={{ margin: "10px 0 0", whiteSpace: "pre-wrap", lineHeight: 1.65, color: "#334155" }}>
                              {shortText(scene.text, 2200)}
                            </p>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <h4 style={{ margin: "0 0 10px", color: "#0f172a" }}>{txt.notes}</h4>
                    {safeArray(workspaceNotes).length === 0 ? (
                      <div style={{ color: "#64748b", fontWeight: 600 }}>{txt.noWorkspaceNotes}</div>
                    ) : (
                      <div style={{ display: "grid", gap: 10 }}>
                        {safeArray(workspaceNotes).map((note) => (
                          <NoteCard key={note.id} note={note} txt={txt} canArchive onArchive={archiveNote} busy={noteBusy} />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
